import { useMutation } from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * Hook to send a message (email/SMS) to specific users or a table via broadcast
 */
export function useSendMessage() {
  return useMutation({
    mutationFn: async ({
      userIds,
      tableId,
      message,
      dryRun = false,
    }: {
      userIds?: string[];
      tableId?: string;
      message: string;
      dryRun?: boolean;
    }): Promise<{
      success: boolean;
      message?: string;
      error?: string;
      results: Array<{
        email: string;
        success: boolean;
        error?: string;
      }>;
    }> => {
      const response: any = await api.post('/admin/send-message', {
        userIds,
        tableId,
        message,
        dryRun,
      });
      return response;
    },
  });
}

export enum EmailTemplate {
  BOOKING_REMINDER = "booking_reminder",
  BOOKING_REMINDER_51H = "booking_reminder_51h",
  BOOKING_REMINDER_72H = "booking_reminder_72h",
  BOOKING_DETAILS = "booking_details",
  BOOKING_ABOUT_TO_HAPPEN = "booking_happening",
  POST_BOOKING_SURVEY = "post_booking_survey",
  BOOKING_UPDATED = "booking_updated",
  UNABLE_TO_BE_SEATED = "unable_to_be_seated",
}

export enum EmailMethod {
  TRANSACTIONAL = "transactional",
  BROADCAST = "broadcast",
}

export interface EmailResult {
  email: string;
  success: boolean;
  request?: any;
  error?: string;
}

export interface TableEmailResponse {
  success: boolean;
  message?: string;
  error?: string;
  results: EmailResult[];
  tableData?: {
    tableId: string;
    tableName: string;
    restaurantName: string;
    restaurantAddress: string;
    neighborhood: string;
    capacity: number;
    eventDate: string;
    eventTime: string;
    users: Array<{
      userId: string;
      email: string;
      firstName: string;
      lastName: string;
    }>;
  };
}

export interface BulkTableEmailResponse {
  success: boolean;
  message?: string;
  error?: string;
  results: Array<{
    tableId: string;
    tableName: string;
    success: boolean;
    emails: EmailResult[];
  }>;
}

export interface UserEmailResponse {
  success: boolean;
  message?: string;
  error?: string;
  recipients: Array<{
    userId: string;
    email: string;
    firstName: string;
    lastName: string;
  }>;
  request?: any;
}

/**
 * Hook to send a templated email broadcast to specific users for an event
 */
export function useSendUserEmails() {
  return useMutation({
    mutationFn: async ({
      eventId,
      userIds,
      template,
      dryRun = false,
      customBroadcastId,
    }: {
      eventId: string;
      userIds: string[];
      template: EmailTemplate;
      dryRun?: boolean;
      customBroadcastId?: string;
    }): Promise<UserEmailResponse> => {
      const response: any = await api.post(
        `/admin/events/${eventId}/send-user-emails`,
        {
          template,
          userIds,
          dryRun,
          customBroadcastId,
        }
      );
      return response;
    },
  });
}

/**
 * Hook to send emails to selected tables
 */
export function useSendTableEmails() {
  return useMutation({
    mutationFn: async ({
      eventId,
      tableIds,
      template,
      dryRun = false,
      customBroadcastId,
    }: {
      eventId: string;
      tableIds: string[];
      template: EmailTemplate;
      dryRun?: boolean;
      /** Optional override of the Customer.io broadcast ID. Falls back to the template default when omitted. */
      customBroadcastId?: string;
    }): Promise<BulkTableEmailResponse> => {
      const response: any = await api.post(`/admin/events/${eventId}/send-table-emails`, {
        template,
        tableIds,
        dryRun,
        customBroadcastId,
      });
      return response;
    },
  });
}

